import { getValidatedPageParam, useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { TParam, usePageParams } from 'hooks/use-page-params';
import { IPaginatedResponse, IProductListItem } from 'pages/supplier-intelligence/utils/types';
import type { UseQueryOptions, UseQueryResult } from 'react-query';

const endpoint = 'ums/v1/supplier-intelligence/products';
export const getSiProductsEndpointIdentifier = endpoint;

type TData = IPaginatedResponse<IProductListItem>;
type TError = AxiosError;
type TQueryKey = [typeof endpoint, { page: TParam; limit: TParam; search: TParam; sort: TParam; supplier: TParam }];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

export function useGetSiProductsApi(options?: Options): ReturnType {
  const toast = useCustomToast();
  const page = getValidatedPageParam();
  const limit = 10;
  const isLoggedIn = useIsLoggedIn();
  const { getPageParams } = usePageParams();
  const { search, sort, supplier } = getPageParams();

  const queryFn = async () => {
    try {
      const params: any = { page, limit };
      if (search) params.search = search;
      if (sort) params.sort = sort;
      if (supplier) params.supplier = supplier;
      const { data } = await http.get(endpoint, { params });
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;
      toast({ description: error.response.data.message, status: 'error' });
      throw error;
    }
  };

  return useQueryWrapper([endpoint, { page, limit, search, sort: sort ?? null, supplier: supplier ?? null }], queryFn, {
    enabled: isLoggedIn,
    ...options,
  });
}
